import React, { useCallback, useMemo, useState } from 'react';
import { createColumnHelper, useReactTable, getCoreRowModel, getSortedRowModel, getFilteredRowModel } from '@tanstack/react-table';
import { toast } from 'react-toastify';
import useTokensLogStore from '../../store/tokens-logs-store';
import useOrganisationStore from '../../store/organisations-store';
import usePaginatedListPage from '../../shared/hooks/usePaginatedListPage';
import useSearchableFilter from '../../shared/hooks/useSearchableFilter';
import DataTable from '../../shared/components/table/DataTable';
import RowActions from '../../shared/components/table/RowActions';
import SearchableSelect from '../../shared/components/table/SearchableSelect';
import ClearFiltersButton from '../../shared/components/table/ClearFiltersButton';
import ListFooter from '../../shared/components/table/ListFooter';
import DeleteModal from '../../shared/components/modal/DeleteModal';
import { DateInput } from '../../shared/components/DateInput';
import { formatDate } from '../../shared/lib/formatters';

const columnHelper = createColumnHelper();

const TokensLogsPage = () => {
  const { tokensLogs, totalTokensLogs, totalPages, fetchTokensLogs, deleteTokensLog } = useTokensLogStore();
  const { searchOrganisations } = useOrganisationStore();

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedTokensLog, setSelectedTokensLog] = useState(null);

  const {
    value: selectedOrgId,
    label: selectedOrgLabel,
    onChange: setOrgSelection,
    clear: clearOrgFilter,
    loadOptions: loadOrgOptions,
  } = useSearchableFilter(searchOrganisations);

  const startDate = useMemo(() => {
    if (!dateFrom) return '';
    const date = new Date(dateFrom);
    date.setHours(0, 0, 0, 0);
    return date.toISOString();
  }, [dateFrom]);

  const endDate = useMemo(() => {
    if (!dateTo) return '';
    const date = new Date(dateTo);
    date.setHours(23, 59, 59, 999);
    return date.toISOString();
  }, [dateTo]);

  const fetchData = useCallback(
    async (page, _search, orgId, startDateArg, endDateArg) => {
      await fetchTokensLogs(page, orgId, startDateArg, endDateArg);
    },
    [fetchTokensLogs]
  );

  const {
    page: currentPage,
    setPage: setCurrentPage,
    isLoading,
  } = usePaginatedListPage({
    fetchItems: fetchData,
    deps: [selectedOrgId, startDate, endDate],
  });

  const handleOpenDeleteModal = (tokensLog) => {
    setSelectedTokensLog(tokensLog);
    setIsDeleteModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsDeleteModalOpen(false);
    setSelectedTokensLog(null);
  };

  const handleDeleteTokensLog = async (id) => {
    try {
      await deleteTokensLog(id);
      toast.success('Tokens Log deleted successfully');
      handleCloseModal();
    } catch (error) {
      console.error('Failed to delete Tokens Log:', error);
      toast.error('Failed to delete Tokens Log. Please try again.');
    }
  };

  const clearFilters = () => {
    clearOrgFilter();
    setDateFrom('');
    setDateTo('');
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor('id', { header: 'ID' }),
      columnHelper.accessor('admin_id', { header: 'Admin ID' }),
      columnHelper.accessor('org_name', { header: 'Organisation' }),
      columnHelper.accessor('tokens', {
        header: 'Tokens',
        cell: (info) => (info.getValue() < 0 ? info.getValue() : `+${info.getValue()}`),
      }),
      columnHelper.accessor('created_at', {
        header: 'Updated At',
        cell: (info) => formatDate(info.getValue()),
      }),
      columnHelper.accessor('device', { header: 'Device' }),
      columnHelper.accessor('browser', { header: 'Browser' }),
      columnHelper.accessor('os', { header: 'OS' }),
      columnHelper.accessor('actions', {
        header: 'Actions',
        cell: ({ row }) => <RowActions onDelete={() => handleOpenDeleteModal(row.original)} />,
      }),
    ],
    []
  );

  const table = useReactTable({
    data: tokensLogs,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  const noLogsFound = table.getRowModel().rows.length === 0;

  return (
    <div className="max-w-screen-3xl mx-auto mt-4">
      <h1 className="text-2xl font-semibold mb-4 py-1">Tokens Logs List</h1>

      <div className="mb-4 flex flex-wrap gap-4">
        <SearchableSelect
          name="org_id"
          value={selectedOrgId}
          selectedLabel={selectedOrgLabel}
          onChange={setOrgSelection}
          loadOptions={loadOrgOptions}
          placeholder="Select Organisation"
        />
        <DateInput value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} placeholder="From Date" />
        <DateInput value={dateTo} onChange={(e) => setDateTo(e.target.value)} placeholder="To Date" />
        <ClearFiltersButton show={!!(selectedOrgId || dateFrom || dateTo)} onClick={clearFilters} />
      </div>

      <DataTable table={table} columnsCount={columns.length} isLoading={isLoading} emptyMessage="No tokens logs found" />

      <ListFooter
        total={totalTokensLogs}
        totalPages={totalPages}
        page={currentPage}
        setPage={setCurrentPage}
        isLoading={isLoading}
        isEmpty={noLogsFound}
        itemLabel="logs"
      />

      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={handleCloseModal}
        data={{ ...selectedTokensLog, dataName: 'tokens log' }}
        onDelete={handleDeleteTokensLog}
      />
    </div>
  );
};

export default TokensLogsPage;
